import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { ThemeContext } from "helpers/contexts.js";
import { VARIENT, SIZE } from "constant";
import { isMobile } from "components/globals";
import { getStyle } from "./HelperFunctions";
import DesktopSelectInput from "./DesktopSelectInput";
import MobileSelectInput from "./MobileSelectInput";

SelectInput.propTypes = {
  options: PropTypes.array.isRequired, //an array of object contains label and value for every option [{label: '', value: ''}]
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf([
    VARIENT.contained,
    VARIENT.outlined,
    VARIENT.empty,
  ]),
  size: PropTypes.oneOf([
    SIZE.small,
    SIZE.smallh,
    SIZE.medium,
    SIZE.large,
    SIZE.xl,
    SIZE.xlh,
  ]),
  rounded: PropTypes.bool,
  disabled: PropTypes.bool,
  errorMsg: PropTypes.string,
  width: PropTypes.string,
  menuWidth: PropTypes.string,
  menuDirection: PropTypes.string,
  withSearch: PropTypes.bool,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  placeholder: PropTypes.string,
  listLabel: PropTypes.string,
  isInitiallyOpen: PropTypes.bool,
};
SelectInput.defaultProps = {
  options: [{ label: "", value: null }],
  variant: VARIENT.outlined,
  size: SIZE.small,
  disabled: false,
  rounded: false,
  withSearch: false,
};

function SelectInput(props) {
  const theme = useContext(ThemeContext);
  const [selectedValue, setSelectedValue] = useState();
  const [focused, setFocused] = useState(false);
  const isSearchable =
    props.options.length > MAX_OPTIONS_WITHOUT_SEARCH || props.withSearch;
  let iconSize, iconColor, iconInlineMargin;

  useEffect(() => {
    if ((props.value || props.value == 0) && props.options.length) {
      const option = props.options.find((option) => {
        return option.value == props.value;
      });
      if (option) {
        setSelectedValue({ label: option.label, value: props.value });
      } else {
        if (props.placeholder) {
          setSelectedValue("");
        } else if (props.options && props.options[0]) {
          setSelectedValue(props.options[0]);
        }
      }
    } else {
      setSelectedValue("");
    }
  }, [props.value, props.options]);

  switch (props.variant) {
    case VARIENT.outlined:
      iconColor = props.disabled
        ? theme.colors.gray.gray500
        : props.errorMsg
        ? theme.colors.red.salmon
        : theme.colors.gray.gray600;
      break;
    case VARIENT.contained:
      iconColor = props.disabled
        ? theme.colors.gray.gray500
        : props.errorMsg
        ? theme.colors.red.fireBrick
        : theme.colors.gray.gray600;
      break;
    case VARIENT.empty:
      iconColor = props.disabled
        ? theme.colors.gray.gray500
        : props.errorMsg
        ? theme.colors.red.salmon
        : theme.colors.gray.gray600;
      break;
    default:
      break;
  }

  switch (props.size) {
    case SIZE.small:
      iconSize = SIZE.xs;
      iconInlineMargin = ICON_MARGIN_INLINE_START.small;
      break;
    case SIZE.smallh:
      iconSize = SIZE.small;
      iconInlineMargin = ICON_MARGIN_INLINE_START.smallh;
      break;
    case SIZE.medium:
      iconSize = SIZE.small;
      iconInlineMargin = ICON_MARGIN_INLINE_START.medium;
      break;
    case SIZE.large:
      iconSize = SIZE.large;
      iconInlineMargin = ICON_MARGIN_INLINE_START.large;
      break;
    case SIZE.xl:
      iconSize = SIZE.xl;
      iconInlineMargin = ICON_MARGIN_INLINE_START.xl;
      break;
    case SIZE.xlh:
      iconSize = SIZE.xl;
      iconInlineMargin = ICON_MARGIN_INLINE_START.xlh;
      break;
    default:
      iconSize = SIZE.xs;
      iconInlineMargin = ICON_MARGIN_INLINE_START.small;
      break;
  }

  function handleOnMenuClosed() {
    setFocused(false);
    if (props.onMenuClose) {
      props.onMenuClose();
    }
  }

  function handleOnMenuOpen() {
    setFocused(true);
    if (props.onMenuOpen) {
      props.onMenuOpen();
    }
  }

  const handleOnChange = (selectedObject) => {
    setFocused(false);
    setSelectedValue(selectedObject);
    props.onChange(selectedObject);
  };

  const colorStyle = getStyle(
    props.disabled,
    props.errorMsg,
    props.variant,
    focused,
    props.size,
    theme,
    props.rounded,
    props.width,
    false,
    props.menuWidth,
    props.menuDirection,
    props.menuHeight
  );

  return isMobile() ? (
    <>
      <MobileSelectInput
        onChange={handleOnChange}
        onMenuOpen={handleOnMenuOpen}
        onMenuClose={handleOnMenuClosed}
        options={props.options}
        colorStyles={colorStyle}
        selectedValue={selectedValue}
        isSearchable={isSearchable}
        iconColor={iconColor}
        iconSize={iconSize}
        isRtl={props.isRtl}
        iconInlineMargin={iconInlineMargin}
        errorMsg={props.errorMsg}
        setFocused={setFocused}
        placeholder={props.placeholder}
        disabled={props.disabled}
        value={selectedValue}
        listLabel={props.listLabel}
        isInitiallyOpen={props.isInitiallyOpen}
      />
    </>
  ) : (
    <DesktopSelectInput
      onChange={handleOnChange}
      onMenuOpen={handleOnMenuOpen}
      onMenuClose={handleOnMenuClosed}
      options={props.options}
      colorStyles={colorStyle}
      selectedValue={selectedValue}
      isSearchable={isSearchable}
      iconColor={iconColor}
      isRtl={props.isRtl}
      iconSize={iconSize}
      iconInlineMargin={iconInlineMargin}
      errorMsg={props.errorMsg}
      placeholder={props.placeholder}
      disabled={props.disabled}
      value={selectedValue}
      isInitiallyOpen={props.isInitiallyOpen}
    />
  );
}

const ICON_MARGIN_INLINE_START = {
  small: "8px",
  smallh: "8px",
  medium: "8px",
  large: "12px",
  xl: "16px",
  xlh: "16px",
};
const MAX_OPTIONS_WITHOUT_SEARCH = 12;

export default React.memo(SelectInput);
